import React, { createContext, useContext, useState, useEffect, type ReactNode } from 'react';
import { userAPI } from '../api/user';
import addressAPI from '../api/addresses';
import type { Address } from '../types';

interface AddressContextType {
  addresses: Address[];
  defaultAddress: Address | null;
  isLoading: boolean;
  error: string | null;
  fetchAddressesIfNeeded: () => Promise<void>;
  setIsLoading: (loading: boolean) => void;
  fetchAddresses: () => Promise<void>;
  addAddress: (addressData: Partial<Address>) => Promise<Address>;
  updateAddress: (addressId: number, addressData: Partial<Address>) => Promise<Address>;
  deleteAddress: (id: number) => Promise<void>;
  updateAddressCoordinates: (
    addressId: number,
    coordinates?: { latitude: number; longitude: number }
  ) => Promise<{ success: boolean; data?: any; error?: string }>;
  setDefaultAddress: (id: number) => Promise<void>;
}

const AddressContext = createContext<AddressContextType | undefined>(undefined);

export const useAddress = () => {
  const context = useContext(AddressContext);
  if (!context) {
    throw new Error('useAddress must be used within AddressProvider');
  }
  return context;
};

export const AddressProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [addresses, setAddresses] = useState<Address[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasLoaded, setHasLoaded] = useState(false);

  const fetchAddressesIfNeeded = async () => {
    if (!hasLoaded) {
      await fetchAddresses();
      setHasLoaded(true);
    }
  };

  const fetchAddresses = async () => {
    setIsLoading(true);
    try {
      const response = await addressAPI.getAddresses();
      // Ensure data is an array
      const data = response.data || [];
      console.log("addresses data:", response)
      setAddresses(Array.isArray(data) ? data : []);
    } catch (error: any) {
      setError(error.message);
      setAddresses([]);
    } finally {
      setIsLoading(false);
    }

  };

  const addAddress = async (addressData: Partial<Address>): Promise<Address> => {
    try {
      const response = await addressAPI.addAddress(addressData);
      setAddresses(prev => [...prev, response.data]);
      return response.data;
    } catch (error: any) {
      setError(error.message);
      throw error;
    }
  };

  const updateAddress = async (id: number, addressData: Partial<Address>) => {
    try {
      const response = await addressAPI.updateAddress(id, addressData);
      setAddresses(prev => prev.map(addr =>
        addr.id === id ? response.data : addr
      ));
    } catch (error: any) {
      setError(error.message);
      throw error;
    }
  };

  const deleteAddress = async (id: number) => {
    try {
      await addressAPI.deleteAddress(id);
      setAddresses(prev => prev.filter(addr => addr.id !== id));
    } catch (error: any) {
      setError(error.message);
      throw error;
    }
  };

  const setDefaultAddress = async (id: number) => {
    try {
      await addressAPI.setDefaultAddress(id);
      setAddresses(prev => prev.map(addr => ({
        ...addr,
        is_default: addr.id === id
      })));
    } catch (error: any) {
      setError(error.message);
      throw error;
    }
  };

  const defaultAddress = addresses && addresses.length > 0
    ? addresses.find(addr => addr.is_default) || null
    : null;

  const updateAddressCoordinates = async (
    addressId: number,
    coordinates?: { latitude: number; longitude: number }
  ) => {
    try {
      let coordsToSave = coordinates;

      // If coordinates not provided, get from browser
      if (!coordsToSave) {
        if (!navigator.geolocation) {
          throw new Error('Geolocation not supported');
        }

        const position = await new Promise<GeolocationPosition>((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject, {
            enableHighAccuracy: true,
            timeout: 10000,
            maximumAge: 0
          });
        });

        coordsToSave = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude
        };
      }

      // Use the addressAPI from to save coordinates
      const response = await addressAPI.updateAddressCoordinates(addressId, coordsToSave);

      if (response.data) {
        // Update local state with new coordinates
        setAddresses(prev => prev.map(addr =>
          addr.id === addressId
            ? {
              ...addr,
              latitude: coordsToSave!.latitude,
              longitude: coordsToSave!.longitude,
              coordinates_saved_at: new Date().toISOString()
            }
            : addr
        ));

        return { success: true, data: response.data };
      } else if (response.error) {
        throw new Error(response.error);
      }

      return { success: false, error: 'Unknown error' };

    } catch (error: any) {
      // Handle specific geolocation errors
      if (error.code === 1) {
        throw new Error('Location permission denied. Please enable location access in your browser settings.');
      } else if (error.code === 2) {
        throw new Error('Location unavailable. Please check your device location settings.');
      } else if (error.code === 3) {
        throw new Error('Location request timed out. Please try again.');
      } else if (error.message.includes('401')) {
        throw new Error('Session expired. Please login again.');
      }
      throw error;
    }
  };

  useEffect(() => {
    fetchAddresses();
  }, []);

  return (
    <AddressContext.Provider value={{
      addresses,
      defaultAddress,
      isLoading,
      error,
      fetchAddressesIfNeeded,
      setIsLoading,
      fetchAddresses,
      addAddress,
      updateAddress,
      deleteAddress,
      updateAddressCoordinates,
      setDefaultAddress,
    }}>
      {children}
    </AddressContext.Provider>
  );
};